import React, { useState, useEffect, useCallback } from 'react';
import { Factory, Search, Plus, Loader2, Eye, ChevronRight, Play, CheckCircle, Truck, Package } from 'lucide-react';
import { useLocation } from 'react-router-dom';
import { productionAPI, bomAPI, branchAPI, fetchPaged } from '../../services/api';
import Pagination from '../ui/Pagination';
import ProductionCreateForm from './ProductionCreateForm';
import ProductionDetailView from './ProductionDetailView';

const statusConfig = {
  'Planned': { bg: 'bg-amber-50', text: 'text-amber-700', border: 'border-amber-200', color: '#f59e0b' },
  'In Progress': { bg: 'bg-blue-50', text: 'text-blue-700', border: 'border-blue-200', color: '#3b82f6' },
  'Completed': { bg: 'bg-emerald-50', text: 'text-emerald-700', border: 'border-emerald-200', color: '#059669' },
  'Dispatched': { bg: 'bg-purple-50', text: 'text-purple-700', border: 'border-purple-200', color: '#7c3aed' },
};

const ProductionModule = () => {
  const [view, setView] = useState('list');
  const [orders, setOrders] = useState([]);
  const [stats, setStats] = useState({ total: 0, planned: 0, in_progress: 0, completed: 0, dispatched: 0 });
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [selectedId, setSelectedId] = useState(null);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [pageSize, setPageSize] = useState(50);
  const handlePageSizeChange = (v) => { setPageSize(v); setPage(1); };
  const location = useLocation();

  // Handle deep-link from ?view= query param (e.g., from Item History)
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const viewId = params.get('view');
    if (viewId) {
      setSelectedId(viewId);
      setView('detail');
      window.history.replaceState({}, document.title, location.pathname);
    }
  }, [location.search, location.pathname]);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const params = { page, page_size: pageSize };
      if (search) params.search = search;
      if (statusFilter) params.status_filter = statusFilter;
      const [result, st] = await Promise.all([
        fetchPaged('/production', params),
        productionAPI.getStats()
      ]);
      setOrders(result.items);
      setTotal(result.total);
      setTotalPages(result.total_pages);
      setStats(st);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, [search, statusFilter, page, pageSize]);

  useEffect(() => { fetchData(); }, [fetchData]);

  if (view === 'create') {
    return <ProductionCreateForm onBack={() => { setView('list'); fetchData(); }} />;
  }
  if (view === 'detail' && selectedId) {
    return <ProductionDetailView prodId={selectedId} onBack={() => { setView('list'); setSelectedId(null); fetchData(); }} />;
  }

  const statCards = [
    { label: 'Total', value: stats.total, color: '#24549c', icon: Factory },
    { label: 'Planned', value: stats.planned, color: '#f59e0b', icon: Package },
    { label: 'In Progress', value: stats.in_progress, color: '#3b82f6', icon: Play },
    { label: 'Completed', value: stats.completed, color: '#059669', icon: CheckCircle },
    { label: 'Dispatched', value: stats.dispatched, color: '#7c3aed', icon: Truck },
  ];

  return (
    <div className="p-5 lg:p-6" data-testid="production-module">
      <div className="flex items-center justify-between mb-5">
        <div>
          <h1 className="text-xl font-bold text-gray-900" style={{ fontFamily: 'DM Sans' }}>Production Planning</h1>
          <p className="text-xs text-gray-500 mt-0.5">Manage production orders, BOM consumption & progress tracking</p>
        </div>
        <button onClick={() => setView('create')} data-testid="new-production-btn"
          className="flex items-center gap-2 px-4 py-2 text-xs font-semibold text-white rounded-lg hover:shadow-lg" style={{ backgroundColor: '#24549c' }}>
          <Plus className="w-3.5 h-3.5" /> New Production Order
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-5">
        {statCards.map(s => (
          <div key={s.label} className="bg-white rounded-xl border border-gray-100 p-4">
            <div className="flex items-center gap-2 mb-2">
              <div className="w-7 h-7 rounded-lg flex items-center justify-center" style={{ backgroundColor: s.color + '15' }}>
                <s.icon className="w-3.5 h-3.5" style={{ color: s.color }} />
              </div>
              <span className="text-[11px] font-medium text-gray-500">{s.label}</span>
            </div>
            <p className="text-2xl font-bold text-gray-900">{s.value}</p>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-3 mb-5">
        <div className="relative flex-1 max-w-xs">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
          <input value={search} onChange={e => { setSearch(e.target.value); setPage(1); }} placeholder="Search orders..." data-testid="production-search"
            className="w-full pl-9 pr-4 py-2 text-xs border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-200" />
        </div>
        <div className="flex gap-1 bg-gray-100 p-1 rounded-xl">
          {['', 'Planned', 'In Progress', 'Completed', 'Dispatched'].map(s => (
            <button key={s} onClick={() => { setStatusFilter(s); setPage(1); }} data-testid={`filter-${s || 'all'}`}
              className={`px-3 py-1.5 text-[11px] font-semibold rounded-lg transition-all ${statusFilter === s ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500'}`}>
              {s || 'All'}
            </button>
          ))}
        </div>
      </div>

      {/* Orders Table */}
      {loading ? (
        <div className="flex items-center justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-blue-600" /></div>
      ) : orders.length === 0 ? (
        <div className="text-center py-16 bg-white rounded-xl border border-gray-100">
          <Factory className="w-12 h-12 text-gray-300 mx-auto mb-3" />
          <p className="text-sm text-gray-500 mb-3">No production orders found</p>
          <button onClick={() => setView('create')} className="px-4 py-2 text-xs font-semibold text-white rounded-lg" style={{ backgroundColor: '#24549c' }}>
            Create First Order
          </button>
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
          <table className="w-full text-xs">
            <thead>
              <tr className="border-b border-gray-100 bg-gray-50/50">
                <th className="text-left px-4 py-3 font-semibold text-gray-500">Order #</th>
                <th className="text-left px-4 py-3 font-semibold text-gray-500">Product</th>
                <th className="text-left px-4 py-3 font-semibold text-gray-500">Production Unit</th>
                <th className="text-left px-4 py-3 font-semibold text-gray-500">BOM</th>
                <th className="text-left px-4 py-3 font-semibold text-gray-500">OC Ref</th>
                <th className="text-center px-4 py-3 font-semibold text-gray-500">Qty</th>
                <th className="text-center px-4 py-3 font-semibold text-gray-500">Materials</th>
                <th className="text-center px-4 py-3 font-semibold text-gray-500">Status</th>
                <th className="text-center px-4 py-3 font-semibold text-gray-500">Dates</th>
                <th className="text-center px-4 py-3 font-semibold text-gray-500"></th>
              </tr>
            </thead>
            <tbody>
              {orders.map(order => {
                const sc = statusConfig[order.status] || statusConfig['Planned'];
                return (
                  <tr key={order.id} className="border-b border-gray-50 hover:bg-gray-50/50 cursor-pointer" onClick={() => { setSelectedId(order.id); setView('detail'); }}
                    data-testid={`prod-row-${order.id}`}>
                    <td className="px-4 py-3 font-mono font-semibold text-gray-900">{order.production_number}</td>
                    <td className="px-4 py-3">
                      <div className="font-semibold text-gray-900">{order.product}</div>
                      {order.fg_item_id && <span className="text-[10px] text-gray-400 font-mono">{order.fg_item_id}</span>}
                    </td>
                    <td className="px-4 py-3">
                      {order.branch_name ? (
                        <>
                          <div className="font-medium text-gray-800 text-[11px]">{order.branch_name}</div>
                          {order.branch_code && <span className="text-[10px] text-gray-400 font-mono">{order.branch_code}</span>}
                        </>
                      ) : <span className="text-gray-300">-</span>}
                    </td>
                    <td className="px-4 py-3 font-mono text-gray-500">{order.bom_number || '-'}</td>
                    <td className="px-4 py-3 text-gray-500">
                      {order.oc_number && order.oc_reference ? (
                        <a href={`/dashboard/sales?tab=order-confirmations&oc=${order.oc_reference}`} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-800 hover:underline font-medium" data-testid={`prod-oc-link-${order.id}`}>{order.oc_number}</a>
                      ) : (order.oc_number || '-')}
                    </td>
                    <td className="px-4 py-3 text-center font-semibold">{order.quantity} {order.unit}</td>
                    <td className="px-4 py-3 text-center text-gray-500">{order.materials?.length || 0}</td>
                    <td className="px-4 py-3 text-center">
                      <span className={`inline-flex px-2 py-0.5 text-[10px] font-semibold rounded-full border ${sc.bg} ${sc.text} ${sc.border}`}>
                        {order.status}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-center text-[10px] text-gray-400">
                      {order.planned_start && <div>Start: {order.planned_start}</div>}
                      {order.planned_end && <div>End: {order.planned_end}</div>}
                    </td>
                    <td className="px-4 py-3 text-center">
                      <Eye className="w-3.5 h-3.5 text-gray-400 mx-auto" />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {orders.length > 0 && <Pagination page={page} totalPages={totalPages} total={total} pageSize={pageSize} onPageChange={setPage} onPageSizeChange={handlePageSizeChange} />}
    </div>
  );
};

export default ProductionModule;
